import React, { useEffect, useState, useRef } from "react";
import { FaTrash } from "react-icons/fa";
import "./CartItem.css";
import { useNavigate } from "react-router-dom";
import { PriceTier } from "../../types/index";
import { useDebounce } from "../../hook/useDebounce";
import api from "../../api";
import { Product } from "../../pages/ProductSections/ProductItem";
import { SizeQuantity } from '@flooron/shared';

interface CartItemProps {
  product: Product;
  onRemove: (productId: number) => void;
  onQuantityChange: (productId: number, newQty: number) => void;
  onSizeQuantityChange?: (productId: number, size: string, newQty: number) => void;
}

const getActivePrice = (item: { priceTiers?: PriceTier[]; cartQuantity?: number }) => {
  const qty = item.cartQuantity ?? 1;
  const tiers = item.priceTiers ?? [];
  if (tiers.length === 0) return 0;
  const matchingTiers = tiers.filter(
    (t) => qty >= t.minQty && (t.maxQty === undefined || qty <= t.maxQty)
  );
  if (matchingTiers.length > 0) {
    return matchingTiers.reduce((prev, curr) =>
      curr.minQty > prev.minQty ? curr : prev
    ).price;
  }
  return tiers.reduce((prev, curr) => (prev.minQty < curr.minQty ? prev : curr)).price;
};

const CartItem: React.FC<CartItemProps> = ({
  product,
  onRemove,
  onQuantityChange,
  onSizeQuantityChange,
}) => {
  const navigate = useNavigate();
  const [localQty, setLocalQty] = useState(Math.max(1, product.cartQuantity ?? 1));
  const [selectedSize, setSelectedSize] = useState<string>('');
  const debouncedQty = useDebounce(localQty, 500);
  const lastSyncedQty = useRef(Math.max(1, product.cartQuantity ?? 1));
  const isInitialMount = useRef(true);

  // Initialize size selection only once
  useEffect(() => {
    if (product.sizeQuantities && product.sizeQuantities.length > 0 && !selectedSize) {
      // Set initial selected size only if not already set
      const cartSize = product.cartSizes?.find(cs => cs.quantity > 0);
      if (cartSize) {
        setSelectedSize(cartSize.size);
      } else {
        setSelectedSize(product.sizeQuantities[0].size);
      }
    }
  }, [product.sizeQuantities, product.cartSizes]); // Only run when size data changes

  useEffect(() => {
    if (product.cartQuantity !== undefined && product.cartQuantity !== null) {
      const normalizedQty = Math.max(1, product.cartQuantity);
      if (normalizedQty !== localQty) {
        setLocalQty(normalizedQty);
        lastSyncedQty.current = normalizedQty;
      }
    }
  }, [product.cartQuantity]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    // Skip cart sync for products with multiple sizes
    if (product.cartSizes && product.cartSizes.length > 1) {
      return;
    }
    if (debouncedQty !== lastSyncedQty.current) {
      const syncCart = async () => {
        try {
          // Use cartItemId if available (for single cart items), otherwise use product.id
          const itemId = product.cartItemId || product.id;
          // For items without sizes, call updateCart with productId and quantity (no size)
          // For items with sizes, the updates are handled by the parent Cart component
          await api.updateCart(itemId.toString(), debouncedQty);
          lastSyncedQty.current = debouncedQty;
        } catch (e) {
          console.error("Cart update failed", e);
        }
      };
      syncCart();
    }
  }, [debouncedQty, product.id, product.cartItemId, product.cartSizes]);

  const handleQtyChange = (newQty: number) => {
    const normalizedQty = Math.max(1, newQty);
    setLocalQty(normalizedQty);
    onQuantityChange(Number(product.id), normalizedQty);
  };

  const selectedCartSizeItem = selectedSize
    ? product.cartSizes?.find(cs => cs.size === selectedSize)
    : undefined;
  const nonZeroSizeCount = product.cartSizes?.filter(cs => cs.quantity > 0).length ?? 0;
  const isLastNonZeroSize = selectedCartSizeItem?.quantity === 1 && nonZeroSizeCount === 1;
  const disableSelectedSizeMinus = !selectedCartSizeItem || selectedCartSizeItem.quantity <= 0 || isLastNonZeroSize;

  const handleSizeQtyChange = async (productId: number, size: string, newQty: number) => {
    if (onSizeQuantityChange) {
      onSizeQuantityChange(productId, size, newQty);
    } else {
      // Fallback: update cart directly
      try {
        await api.updateCart(productId.toString(), newQty, size);
      } catch (e) {
        console.error("Size quantity update failed", e);
      }
    }
  };

  const handleViewDetail = () => navigate(`/product/${product.id}`);

  const handleRemove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove(Number(product.id));
  };

  const price = getActivePrice({ priceTiers: product.priceTiers, cartQuantity: localQty });

  const firstImage =
    product?.images?.[0] && product.images[0] !== ""
      ? product.images[0]
      : "/placeholder-product.png";

  return (
    <div className="cart-item-container">
      <div className="cart-item-images" onClick={handleViewDetail}>
        <img
          src={`https://api.flooron.in/${firstImage}`}
          alt={product?.name || "Product"}
          onError={(e) => { (e.target as HTMLImageElement).src = "/placeholder-product.png"; }}
        />
      </div>
      <div className="cart-item-info">
        <p onClick={handleViewDetail}>{product?.brand || "Product"}</p>
        <h1 onClick={handleViewDetail}>{product?.name || "Untitled Product"}</h1>
        <h2>₹{price.toLocaleString("en-IN")}</h2>
        <p className="cart-item-desc">{product?.description || "No description available."}</p>
        
        {/* Size Selector with Quantity Control */}
        {product.sizeQuantities && product.sizeQuantities.length > 0 ? (
          <div className="cart-item-size-selector">
            <div className="cart-item-size-label">Select Size</div>
            <div className="cart-item-size-options">
              {product.sizeQuantities.map((sizeItem, index) => {
                // Find if this size is in cart
                const cartSizeItem = product.cartSizes?.find(cs => cs.size === sizeItem.size);
                const quantity = cartSizeItem ? cartSizeItem.quantity : 0;
                
                return (
                  <button
                    key={index}
                    className={`cart-item-size-option ${selectedSize === sizeItem.size ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setSelectedSize(sizeItem.size); }}
                  >
                    {sizeItem.size} ({quantity})
                  </button>
                );
              })}
            </div>
            {selectedSize && (
              <div className="cart-size-quantity-control">
                <div className="qty-picker">
                  <button 
                    className="cart-size-quantity-btn"
                    disabled={disableSelectedSizeMinus}
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      if (!selectedCartSizeItem) return;
                      handleSizeQtyChange(Number(product.id), selectedSize, selectedCartSizeItem.quantity - 1);
                    }}
                  >
                    −
                  </button>
                  <input 
                    type="number" 
                    className="qty-input" 
                    value={product.cartSizes?.find(cs => cs.size === selectedSize)?.quantity || 0}
                    onChange={(e) => {
                      let newQty = Math.max(0, parseInt(e.target.value) || 0);
                      if (newQty === 0 && isLastNonZeroSize) {
                        newQty = 1;
                      }
                      handleSizeQtyChange(Number(product.id), selectedSize, newQty);
                    }}
                    min="0"
                  />
                  <button 
                    className="cart-size-quantity-btn"
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      const cartSizeItem = product.cartSizes?.find(cs => cs.size === selectedSize);
                      const nextQty = cartSizeItem ? cartSizeItem.quantity + 1 : 1;
                      handleSizeQtyChange(Number(product.id), selectedSize, nextQty);
                    }}
                  >
                    +
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="cart-item-quantity-controls">
            <div className="cart-item-quantity-label">Qty</div>
            <div className="cart-item-quantity-wrapper">
              <button
                className="cart-item-quantity-btn"
                onClick={(e) => { e.stopPropagation(); handleQtyChange(Math.max(1, localQty - 1)); }}
                disabled={localQty <= 1}
                aria-label="Decrease quantity"
              >−</button>
              <div className="cart-item-quantity-display">{localQty}</div>
              <button
                className="cart-item-quantity-btn"
                onClick={(e) => { e.stopPropagation(); handleQtyChange(localQty + 1); }}
                disabled={localQty >= (product.availableQuantity || 99)}
                aria-label="Increase quantity"
              >+</button>
              <div className="cart-item-quantity-subtitle">
                {product.availableQuantity ? `${product.availableQuantity} available` : "In stock"}
              </div>
            </div>
          </div>
        )}
        <div className="cart-item-buttons">
          <button className="cart-item-add outline" onClick={handleViewDetail}>
            View Details
          </button>
          <button className="cart-item-add remove" onClick={handleRemove}>
            <FaTrash size={14} style={{ marginRight: 6 }} /> Remove
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartItem;